import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  Flame,
  RotateCcw,
  Sparkles,
  Undo2,
  XCircle,
} from 'lucide-react';
import {
  abandonSentenceScrambleRun,
  answerSentenceScramble,
  completeSentenceScrambleRun,
  createSentenceScrambleRun,
  getSentenceScrambleRun,
  type SentenceScrambleComplete,
  type SentenceScrambleGrade,
  type SentenceScrambleQuestion,
  type SentenceScrambleRun,
} from '../api/learning';
import { ArcadeSummaryView } from '../components/ui';
import './SentenceScrambleGame.css';

type PendingAnswer = {
  questionId: string;
  orderedTokens: string[];
};

function isScrambleQuestion(
  question: SentenceScrambleRun['questions'][number]
): question is SentenceScrambleQuestion {
  return question.type === 'sentence-scramble';
}

function secondsUntil(deadlineAt: string | null): number | null {
  if (!deadlineAt) return null;
  return Math.max(0, Math.ceil((Date.parse(deadlineAt) - Date.now()) / 1000));
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function SentenceScrambleGame() {
  const [relaxed, setRelaxed] = useState(false);
  const [run, setRun] = useState<SentenceScrambleRun | null>(null);
  const [answeredIds, setAnsweredIds] = useState<string[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [pending, setPending] = useState<PendingAnswer | null>(null);
  const [grade, setGrade] = useState<SentenceScrambleGrade | null>(null);
  const [summary, setSummary] = useState<SentenceScrambleComplete | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'start' | 'grade' | 'recover' | null>(null);
  const finalizingRef = useRef(false);

  const questions = useMemo(() => run?.questions.filter(isScrambleQuestion) ?? [], [run]);
  const question = questions.find(item => !answeredIds.includes(item.id)) ?? null;
  const orderedTokens = question ? selectedIndices.map(index => question.tokens[index] ?? '') : [];

  useEffect(() => {
    if (!run || run.timing === 'relaxed' || !run.deadlineAt || summary) {
      setRemainingSeconds(null);
      return;
    }
    const update = () => setRemainingSeconds(secondsUntil(run.deadlineAt));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [run, summary]);

  useEffect(() => {
    if (remainingSeconds !== 0 || !run || pending || finalizingRef.current) return;
    let cancelled = false;
    finalizingRef.current = true;
    setBusy(true);
    completeSentenceScrambleRun(run.runId)
      .then(result => {
        if (!cancelled) setSummary(result);
      })
      .catch(() => {
        if (!cancelled) setError('recover');
      })
      .finally(() => {
        finalizingRef.current = false;
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pending, remainingSeconds, run]);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const created = await createSentenceScrambleRun(relaxed ? 'relaxed' : 'standard');
      setRun(created);
      setAnsweredIds([]);
      setSelectedIndices([]);
      setPending(null);
      setGrade(null);
      setSummary(null);
      setRemainingSeconds(secondsUntil(created.deadlineAt));
    } catch {
      setError('start');
    } finally {
      setBusy(false);
    }
  }

  function applyGrade(result: SentenceScrambleGrade) {
    setRun(current =>
      current
        ? {
            ...current,
            score: result.score,
            combo: result.combo,
            bestCombo: result.bestCombo,
            livesRemaining: result.livesRemaining,
            deadlineAt: result.deadlineAt,
            status: result.status,
            personalBest: result.personalBest,
          }
        : current
    );
    setAnsweredIds(current =>
      current.includes(result.questionId) ? current : [...current, result.questionId]
    );
    setSelectedIndices([]);
    setPending(null);
    setGrade(result);
    setError(null);
    if (result.complete && result.verifiedScore !== null) {
      setSummary({
        runId: run?.runId ?? '',
        status: result.status,
        verifiedScore: result.verifiedScore,
        score: result.score,
        bestCombo: result.bestCombo,
        correctCount: result.correctCount,
        answeredCount: result.answeredCount,
        accuracy: result.accuracy,
        personalBest: result.personalBest ?? result.verifiedScore,
        isNewPersonalBest: result.isNewPersonalBest,
        streakDays: run?.streakDays ?? 0,
        ghostOutcome: null,
      });
    }
  }

  async function submit() {
    if (!run || !question || busy || orderedTokens.length !== question.tokens.length) return;
    const answer = { questionId: question.id, orderedTokens };
    setPending(answer);
    setBusy(true);
    setError(null);
    try {
      applyGrade(await answerSentenceScramble(run.runId, answer.questionId, answer.orderedTokens));
    } catch {
      setError('grade');
    } finally {
      setBusy(false);
    }
  }

  async function retry() {
    if (!run || !pending) return;
    setBusy(true);
    setError(null);
    try {
      applyGrade(
        await answerSentenceScramble(run.runId, pending.questionId, pending.orderedTokens)
      );
    } catch {
      setError('grade');
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    if (!run) return;
    setBusy(true);
    setError(null);
    try {
      const recovered = await getSentenceScrambleRun(run.runId);
      setRun(current => ({ ...recovered, streakDays: current?.streakDays ?? 0 }));
      setAnsweredIds(recovered.gradedQuestionIds);
      setSelectedIndices([]);
      setPending(null);
      if (recovered.status !== 'in_progress' && recovered.verifiedScore !== null) {
        setSummary({
          runId: recovered.runId,
          status: recovered.status,
          verifiedScore: recovered.verifiedScore,
          score: recovered.score,
          bestCombo: recovered.bestCombo,
          correctCount: recovered.correctCount,
          answeredCount: recovered.answeredCount,
          accuracy: recovered.accuracy,
          personalBest: recovered.personalBest ?? recovered.verifiedScore,
          isNewPersonalBest: recovered.isNewPersonalBest,
          streakDays: run.streakDays,
          ghostOutcome: null,
        });
      }
    } catch {
      setError('recover');
    } finally {
      setBusy(false);
    }
  }

  async function abandon() {
    if (!run) return;
    setBusy(true);
    try {
      await abandonSentenceScrambleRun(run.runId);
      setRun(null);
      setAnsweredIds([]);
      setSelectedIndices([]);
      setPending(null);
      setGrade(null);
      setError(null);
    } catch {
      setError('recover');
    } finally {
      setBusy(false);
    }
  }

  if (summary) {
    return (
      <ArcadeSummaryView
        heading="Sentences complete"
        verifiedScore={summary.verifiedScore}
        accuracy={summary.accuracy}
        correctCount={summary.correctCount}
        answeredCount={summary.answeredCount}
        isNewPersonalBest={summary.isNewPersonalBest}
        additionalStats={[{ label: 'Best combo', value: `×${summary.bestCombo}` }]}
        playAgainDisabled={busy}
        onPlayAgain={() => void start()}
      />
    );
  }

  if (!run) {
    return (
      <section aria-labelledby="scramble-heading">
        <div className="learning-section-heading">
          <div>
            <h2 id="scramble-heading">Sentence Scramble</h2>
            <p>Put each set of shuffled tokens into a complete sentence.</p>
          </div>
        </div>
        {error === 'start' && <ScrambleError message="We could not start Sentence Scramble." />}
        <div className="scramble-start-card">
          <Clock3 aria-hidden="true" />
          <p>
            {relaxed
              ? 'Take your time. The server still checks every sentence.'
              : 'Build each sentence before the round clock ends.'}
          </p>
          <label className="scramble-toggle">
            <input
              type="checkbox"
              checked={relaxed}
              onChange={event => setRelaxed(event.target.checked)}
            />
            Relaxed — no timer
          </label>
          <button className="scramble-primary" onClick={() => void start()} disabled={busy}>
            {busy ? 'Shuffling tokens…' : 'Start Sentence Scramble'}
          </button>
          {busy && (
            <span className="sr-only" role="status">
              Building Sentence Scramble round…
            </span>
          )}
        </div>
      </section>
    );
  }

  if (!question) {
    return (
      <section className="scramble-empty" aria-labelledby="scramble-empty-title">
        <Sparkles aria-hidden="true" />
        <h2 id="scramble-empty-title">No sentences ready yet</h2>
        <p>Save a longer translated sentence, then return to practice it here.</p>
        <button className="scramble-primary" onClick={() => void abandon()} disabled={busy}>
          Back to start
        </button>
      </section>
    );
  }

  return (
    <section aria-labelledby="scramble-board-heading" aria-busy={busy}>
      <p className="sr-only" role="status">
        Sentence Scramble round ready.
      </p>
      <div className="scramble-hud">
        <div>
          <span>Score</span>
          <strong>{run.score.toLocaleString()}</strong>
        </div>
        <div>
          <span>Combo</span>
          <strong>
            <Flame aria-hidden="true" /> ×{run.combo}
          </strong>
        </div>
        <div>
          <span>Streak</span>
          <strong>
            <CalendarDays aria-hidden="true" />
            {run.streakDays > 0
              ? `${run.streakDays} ${run.streakDays === 1 ? 'day' : 'days'}`
              : 'Start today'}
          </strong>
        </div>
        <div>
          <span>Time</span>
          <strong>{remainingSeconds === null ? 'No timer' : formatTime(remainingSeconds)}</strong>
        </div>
      </div>

      <div className="learning-section-heading">
        <div>
          <h2 id="scramble-board-heading">Build the sentence</h2>
          <p>{question.prompt}</p>
        </div>
        <span className="learning-count">
          {answeredIds.length + 1} of {run.totalQuestions}
        </span>
      </div>

      {grade && (
        <div
          className={`scramble-feedback scramble-feedback--${grade.correct ? 'correct' : 'incorrect'}`}
          role="status"
          aria-live="polite"
        >
          {grade.correct ? <CheckCircle2 aria-hidden="true" /> : <XCircle aria-hidden="true" />}
          <span>
            {grade.correct
              ? `Correct · +${grade.scoreDelta} points · Combo ×${grade.combo}`
              : `Not quite. Correct sentence: ${grade.correctAnswer}`}
          </span>
        </div>
      )}

      {error === 'grade' && pending && (
        <div className="scramble-recovery" role="alert">
          <p>Your sentence was not confirmed. The round is paused at its last server state.</p>
          <button onClick={() => void retry()} disabled={busy}>
            Retry sentence
          </button>
          <button onClick={() => void resume()} disabled={busy}>
            Resume from server
          </button>
          <button onClick={() => void abandon()} disabled={busy}>
            End unverified run
          </button>
        </div>
      )}
      {error === 'recover' && (
        <div className="scramble-recovery" role="alert">
          <p>The round could not be recovered.</p>
          <button onClick={() => void resume()} disabled={busy}>
            Try resume again
          </button>
          <button onClick={() => void abandon()} disabled={busy}>
            End unverified run
          </button>
        </div>
      )}

      <div className="scramble-order" aria-label="Your sentence">
        <h3>Your sentence</h3>
        <div className="scramble-token-row">
          {selectedIndices.length === 0 ? (
            <p className="scramble-placeholder">Choose a token to begin.</p>
          ) : (
            selectedIndices.map((tokenIndex, position) => (
              <button
                key={tokenIndex}
                className="scramble-token scramble-token--placed"
                onClick={() =>
                  setSelectedIndices(current => current.filter(index => index !== tokenIndex))
                }
                disabled={busy || remainingSeconds === 0}
                aria-label={`Remove ${question.tokens[tokenIndex]} from position ${position + 1}`}
              >
                <span className="scramble-position" aria-hidden="true">
                  {position + 1}
                </span>
                {question.tokens[tokenIndex]}
                <Undo2 aria-hidden="true" />
              </button>
            ))
          )}
        </div>
        <p className="scramble-progress" role="status" aria-live="polite">
          {selectedIndices.length} of {question.tokens.length} tokens placed
        </p>
      </div>

      <div className="scramble-bank" aria-label="Shuffled tokens">
        <h3>Shuffled tokens</h3>
        <div className="scramble-token-row">
          {question.tokens.map((token, index) =>
            selectedIndices.includes(index) ? null : (
              <button
                key={index}
                className="scramble-token"
                onClick={() => setSelectedIndices(current => [...current, index])}
                disabled={busy || remainingSeconds === 0}
                aria-label={`Add ${token} to position ${selectedIndices.length + 1}`}
              >
                {token}
              </button>
            )
          )}
        </div>
      </div>

      <div className="scramble-actions">
        <button
          className="scramble-primary"
          onClick={() => void submit()}
          disabled={
            busy || selectedIndices.length !== question.tokens.length || remainingSeconds === 0
          }
        >
          Check sentence
        </button>
        <button
          className="scramble-secondary"
          onClick={() => setSelectedIndices([])}
          disabled={busy || selectedIndices.length === 0 || remainingSeconds === 0}
        >
          <RotateCcw aria-hidden="true" /> Clear order
        </button>
      </div>

      <button className="scramble-abandon" onClick={() => void abandon()} disabled={busy}>
        End run without saving a score
      </button>
    </section>
  );
}

function ScrambleError({ message }: { message: string }) {
  return (
    <div className="learning-notice learning-notice--error" role="alert">
      <XCircle aria-hidden="true" /> <span>{message}</span>
    </div>
  );
}
